import type {
  ChangeEvent,
  ChangeEventHandler,
  Key,
  RefObject,
  SyntheticEvent,
} from 'react';
import { useRef, useState } from 'react';
import mergeRefs from 'react-merge-refs';
import { useUID } from 'react-uid';
import { AnimatePresence, motion } from 'framer-motion';
import { noop } from 'lodash';
import styled from 'styled-components';
import type {
  AccountUser,
  Role,
  SuggestedMember,
} from '@frameio/graphql-client/generated';
import { useAccountSubscriptionDetails } from '@frameio/plan-management/plans/hooks/useAccountSubscriptionDetails';
import { EmailMessageTextarea } from '@frameio/shared/components';
import { GraphemeLimit } from '@frameio/shared/components/GraphemeLimit';
import { useAccountManagedByAdobe } from '@frameio/shared/hooks';
import { useIsEnterpriseAccount } from '@frameio/shared/plans/hooks/useIsEnterpriseAccount';
import { GLYPHS } from '@frameio/utils/constants';
import { formatBytes } from '@frameio/utils/formatters';
import type { ScrollContainerProps } from '@frameio/vapor';
import {
  AlertInline,
  Box,
  Button,
  DialogActions,
  DialogFooter,
  ScrollContainer,
  Separator,
  Spinner,
  Text,
  textStyles,
  theme,
  useTimeDeferredMount,
} from '@frameio/vapor';
import { IconMagnifyingglass } from '@frameio/vapor-icons';
import { INVITE_MESSAGE_MAX_LENGTH } from '../../utils';
import TEXT from './generated/TEXT/MemberAccessForm';
import { getInputDetailVariant } from './getInputDetailVariant';
import { getMemberSeatRateLabel } from './getMemberSeatRateLabel';
import styles from './MemberAccessForm.module.css';
import { MemberAccessRoleSelect } from './MemberAccessRoleSelect';
import { MemberAccessRowList } from './MemberAccessRowList';
import { StorageCounter } from './StorageCounter';
import type { UserAccessSearchInputProps } from './UserAccessSearchInput';
import { UserAccessSearchInput } from './UserAccessSearchInput';
import type { UserLicenseTypeSelectProps } from './UserLicenseTypeSelect';
import { UserLicenseTypeSelect } from './UserLicenseTypeSelect';

type LicenseType = UserLicenseTypeSelectProps['value'];

export type MemberAccessFormProps = {
  availableRoles?: Array<Role>;
  canInviteGuests?: boolean;
  currentInputQuery?: string;
  currentRoleId?: Role['id'];
  deferRender?: boolean;
  guestSeatLimit?: number;
  hasNewMembers?: boolean;
  hasInvalidEmails?: boolean;
  hasNoClientSideMatches?: boolean;
  inputRef: RefObject<HTMLInputElement | null>;
  inputTokens: Array<UserAccessSearchInputProps['tokens'][number]>;
  isGuestOverLimit?: boolean;
  isLoading?: boolean;
  isMemberOverLimit?: boolean;
  isRestrictedProject?: boolean;
  isRestrictedFolder?: boolean;
  isSubmitDisabled?: boolean;
  isOnTrial?: boolean;
  licenseType?: LicenseType;
  memberSeatLimit?: number;
  message?: string;
  onCancel: () => void;
  onChangeLicenseType?: (seat: LicenseType) => void;
  onChangeRole?: (id: Role['id']) => void;
  onEmailMessageChange?: ChangeEventHandler<HTMLTextAreaElement>;
  onEnterKey: () => void;
  onInputBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onMemberClick?: (evt: SyntheticEvent<HTMLButtonElement, Event>) => void;
  onPendingMemberClick?: (textInput: string) => void;
  onRemoveMember: (id: string) => void;
  onSearchQueryChange: (evt: ChangeEvent<HTMLInputElement>) => void;
  onSearchQueryPaste: (evt: React.ClipboardEvent<HTMLInputElement>) => void;
  onSubmit: (licenseType: LicenseType) => void;
  planPeriod?: string;
  resourceType?: string;
  scrollContainerHeight?: string | ScrollContainerProps['height'];
  searchInputPlaceholder?: string;
  searchInputTitle?: string;
  shouldManageEmailMessage?: boolean;
  submitLabel?: string;
  subscriptionCost?: number;
  suggestedMembers?: Array<SuggestedMember | AccountUser>;
  totalNewUsers?: number;
};
TEXT.DEFINE({
  /** Title for the member access form */
  title: (name: string) => `Invite Members to ${name}`,
  /** Label for the cancel button */
  labelCancel: 'Cancel',
  /** Label for the submit button */
  labelSubmit: 'Add',
  /** Label for the notify button */
  labelNotify: 'Add custom message',
  /** Label for the spinner */
  labelSpinner: 'Suggested Members loading',
  search: {
    /** Title for the search input */
    title: 'Suggested',
    /** Message for no matches */
    noMatches: (name: string) => `No result found for "${name}"`,
    /** Title for the no matches message */
    noMatchesTitle: 'Search result',
    /** Placeholder for the search input */
    placeholder: 'Name or email',
    /** Label for the loading spinner */
    loading: 'loading…',
  },
  message: {
    /** Title for the message input */
    title: 'Message',
    /** Placeholder for the message input */
    placeholder: 'Add a message (optional)',
  },
  /** Label for the existing members details */
  existingMembersDetails: 'Add an existing Member',
  /** Label for the existing project members details */
  existingProjectMembersDetails: 'Add an existing Project Member',
  /** Label for the new or existing members details */
  newOrExistingMembersDetails: 'Add a new or existing Member',
  /** Label for the new member details */
  newMemberDetails: 'New members will be added for\u00A0',
  /** Label for the new member seat rate */
  newMemberSeatRate: (
    rate: string,
    storageIncrement?: string,
    isOnTrial?: boolean
  ) =>
    `${
      storageIncrement
        ? `New members come with ${storageIncrement} storage for ${rate} (plus tax)`
        : `New members will be added for ${rate} (plus tax)`
    }${isOnTrial ? ' after free trial period' : ''}.`,
  /** Label for the guest license details */
  guestLicenseDetails: 'Add a free Guest for single-project access',
  /** Label for the invalid email */
  invalidEmail: 'Invalid email address',
  /** Label for the guest over limit */
  guestOverLimit: (limit: number) =>
    `Exceeded account limit of ${limit} free Guests.`,
  /** Label for the member over limit */
  memberOverLimit: (limit: number) =>
    `Exceeded account limit of ${limit} Members.`,
  /** Label for the subscribe to */
  subscribeTo: 'subscribe to',
  /** Label for the add more members */
  addMoreMembers: 'To add more members,',
  /** Label for the add more guests */
  addMoreGuests: 'To add more free guests,',
  /** Label for the adobe managed account */
  adobeManagedAccount: (resourceType: string) =>
    `To add new members to this ${resourceType.toLowerCase()}, contact your Adobe administrator.`,
  /** Label for the non existing members for folder */
  nonExistingMembersForFolder:
    'Only existing Project members can be added to Restricted folders',
  /** Label for the restricted folder alert text */
  restrictedFolderAlertText:
    'To specify which project members have access to this folder, you’ll need to make this folder restricted.',
  /** Label for the no suggested members for folder */
  noSuggestedMembersForFolder:
    'There are no project members left to invite. The account owner and content admins already have full access to all restricted folders.',
});

const DEFAULT_SCROLL_HEIGHT = '238px';

export const LicenseAndRoleSelect = ({
  availableRoles,
  onRoleChange,
  onLicenseChange,
  roleId,
  licenseType,
}: {
  availableRoles: Array<Role>;
  onRoleChange: (id: Role['id']) => void;
  onLicenseChange: (seat: LicenseType) => void;
  roleId: Role['id'];
  licenseType: LicenseType;
}) => (
  <LicenseRoleContainer
    alignItems="center"
    display="flex"
    justifyContent="space-between"
    padding={8}
  >
    <UserLicenseTypeSelect onChange={onLicenseChange} value={licenseType} />
    <Box alignItems="center" display="flex" height={32}>
      <MemberAccessRoleSelect
        availableRoles={availableRoles}
        onChange={onRoleChange}
        roleId={roleId}
      />
    </Box>
  </LicenseRoleContainer>
);

type MemberAccessInputDetailsProps = {
  guestSeatLimit?: number;
  hasNewMembers?: boolean;
  memberSeatLimit?: number;
  invalidEmail: boolean;
  licenseType: LicenseType;
  memberSeatRate?: string;
  isOnTrial?: boolean;
  resourceType?: string;
  totalNewUsers?: number;
};

const DETAILS_ANIMATION_DURATION = 0.2;

const InputDetailsContainer = ({
  children,
  key,
}: {
  children: React.ReactNode;
  key: Key;
}) => (
  <motion.div
    key={key}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    initial={{ opacity: 0 }}
    style={{ position: 'absolute', top: 0 }}
    transition={{
      duration: DETAILS_ANIMATION_DURATION,
      ease: 'easeOut',
    }}
  >
    {children}
  </motion.div>
);

export const MemberAccessInputDetails = ({
  guestSeatLimit,
  hasNewMembers = false,
  memberSeatLimit,
  invalidEmail,
  licenseType,
  memberSeatRate,
  isOnTrial = false,
  resourceType,
  totalNewUsers = 0,
}: MemberAccessInputDetailsProps) => {
  const isManagedByAdobe = useAccountManagedByAdobe();
  const {
    isMemberSeatUpgradeAllowed,
    isGuestSeatUpgradeAllowed,
    includedMemberSeatsAvailable,
    subscriptionUpgradeText,
    onUpgradePlanTier,
    perUserStorageIncrement,
    planTier,
    seatLimit,
  } = useAccountSubscriptionDetails({});

  const inputDetailVariant = getInputDetailVariant({
    guestSeatLimit,
    hasNewMembers,
    includedMemberSeatsAvailable,
    invalidEmail,
    isManagedByAdobe,
    licenseType,
    memberSeatLimit,
    memberSeatRate,
    resourceType,
    seatLimit,
    totalNewUsers,
  });

  const showDynamicStorage =
    (planTier === 'PRO' || planTier === 'TEAM') && perUserStorageIncrement > 0;

  let details = '';
  if (isManagedByAdobe) {
    details = TEXT.existingMembersDetails;
  } else if (licenseType === 'GUEST') {
    details = TEXT.guestLicenseDetails;
  } else {
    // display a default detail message based on project/folder access
    details =
      resourceType === 'FolderAsset'
        ? TEXT.existingProjectMembersDetails
        : TEXT.newOrExistingMembersDetails;
  }

  const marginBottomOffset = -24;

  return (
    <Box minHeight={24} paddingBottom={8} position="relative">
      <AnimatePresence mode="wait">
        {inputDetailVariant === 'FOLDER_DETAILS' && (
          <InputDetailsContainer key="FOLDER_DETAILS">
            <Box paddingBottom={12}>
              <Text color="error" fontWeight="regular" variant="caption">
                {TEXT.nonExistingMembersForFolder}
              </Text>
            </Box>
          </InputDetailsContainer>
        )}
        {inputDetailVariant === 'INVALID_EMAIL_DETAILS' && (
          <InputDetailsContainer key="INVALID_EMAIL_DETAILS">
            <Box paddingBottom={12}>
              <Text color="error" fontWeight="regular" variant="caption">
                {TEXT.invalidEmail}
              </Text>
            </Box>
          </InputDetailsContainer>
        )}
        {inputDetailVariant === 'ADOBE_MANAGED_DETAILS' && (
          <InputDetailsContainer key="ADOBE_MANAGED_DETAILS">
            <Box display="flex" paddingBottom={12}>
              <Text color="tertiary" fontWeight="regular" variant="caption">
                {TEXT.adobeManagedAccount(resourceType ?? '')}
              </Text>
            </Box>
          </InputDetailsContainer>
        )}
        {inputDetailVariant === 'MEMBER_SEAT_LIMIT_DETAILS' && (
          <InputDetailsContainer key="MEMBER_SEAT_LIMIT_DETAILS">
            <Box paddingBottom={12}>
              <div className={styles.inline}>
                <Text color="error" fontWeight="regular" variant="caption">
                  {TEXT.memberOverLimit(memberSeatLimit ?? 0)}
                </Text>

                {isMemberSeatUpgradeAllowed && (
                  <div className={styles.inline}>
                    <Text fontWeight="regular" variant="caption">
                      {`${GLYPHS.nbsp}${TEXT.addMoreMembers}${GLYPHS.nbsp}`}
                    </Text>
                    <UpgradeButton
                      as={Text}
                      onClick={() => {
                        onUpgradePlanTier?.();
                      }}
                      variant="link"
                    >
                      {`${TEXT.subscribeTo} ${subscriptionUpgradeText()}`}
                    </UpgradeButton>
                    <Text fontWeight="regular" variant="caption">
                      .
                    </Text>
                  </div>
                )}
              </div>
            </Box>
          </InputDetailsContainer>
        )}

        {inputDetailVariant === 'NEW_MEMBER_DETAILS' && (
          <motion.div
            key="NEW_MEMBER_DETAILS"
            animate={{
              opacity: 1,
              marginBottom: 0,
            }}
            exit={{
              opacity: 0,
              marginBottom: marginBottomOffset,
            }}
            initial={{
              opacity: 0,
              marginBottom: marginBottomOffset,
            }}
            style={{ overflow: 'hidden', height: isOnTrial ? '48px' : '40px' }}
            transition={{
              duration: DETAILS_ANIMATION_DURATION,
              ease: 'easeOut',
            }}
          >
            <Box
              alignItems="center"
              backgroundColor="component"
              borderRadius={8}
              display="flex"
              height="100%"
              justifyContent="space-between"
              overflow="hidden"
              padding={12}
            >
              <Box display="flex" width="100%">
                <Text
                  color="body"
                  display="inline"
                  fontWeight="regular"
                  variant="caption"
                >
                  {TEXT.newMemberSeatRate(
                    memberSeatRate ?? '',
                    showDynamicStorage
                      ? formatBytes(perUserStorageIncrement)
                      : undefined,
                    isOnTrial
                  )}
                </Text>
              </Box>

              {showDynamicStorage && (
                <StorageCounter
                  align="right"
                  fontWeight="medium"
                  value={totalNewUsers * perUserStorageIncrement}
                  variant="secondary"
                />
              )}
            </Box>
          </motion.div>
        )}
        {inputDetailVariant === 'GUEST_SEAT_LIMIT_DETAILS' && (
          <InputDetailsContainer key="GUEST_SEAT_LIMIT_DETAILS">
            <Box paddingBottom={12}>
              <div className={styles.inline}>
                <Text color="error" fontWeight="regular" variant="caption">
                  {TEXT.guestOverLimit(guestSeatLimit ?? 0)}
                </Text>
                {isGuestSeatUpgradeAllowed && (
                  <div className={styles.inline}>
                    <Text fontWeight="regular" variant="caption">
                      {`${GLYPHS.nbsp}${TEXT.addMoreGuests}${GLYPHS.nbsp}`}
                    </Text>
                    <UpgradeButton
                      as={Text}
                      onClick={onUpgradePlanTier}
                      variant="link"
                    >
                      {`${TEXT.subscribeTo} ${subscriptionUpgradeText()}`}
                    </UpgradeButton>
                    <Text fontWeight="regular" variant="caption">
                      .
                    </Text>
                  </div>
                )}
              </div>
            </Box>
          </InputDetailsContainer>
        )}
        {!inputDetailVariant && (
          <InputDetailsContainer key="NO_INPUT_VARIANT">
            <Box paddingBottom={12}>
              <Text color="tertiary" fontWeight="regular" variant="caption">
                {details}
              </Text>
            </Box>
          </InputDetailsContainer>
        )}
      </AnimatePresence>
    </Box>
  );
};

export const MemberAccessForm = ({
  availableRoles,
  currentInputQuery = '',
  currentRoleId,
  guestSeatLimit,
  memberSeatLimit,
  licenseType,
  deferRender = true,
  canInviteGuests = false,
  hasNewMembers = false,
  hasNoClientSideMatches,
  inputRef: customInputRef,
  isLoading = false,
  isSubmitDisabled,
  isOnTrial = false,
  message = '',
  onCancel,
  onChangeRole = noop,
  onChangeLicenseType = noop,
  onEmailMessageChange = noop,
  onEnterKey,
  onInputBlur,
  onMemberClick = noop,
  onPendingMemberClick = noop,
  onRemoveMember,
  onSearchQueryChange,
  onSearchQueryPaste,
  onSubmit,
  scrollContainerHeight = DEFAULT_SCROLL_HEIGHT,
  inputTokens = [],
  hasInvalidEmails = false,
  isGuestOverLimit = false,
  isMemberOverLimit = false,
  resourceType,
  searchInputPlaceholder,
  searchInputTitle,
  shouldManageEmailMessage = true,
  submitLabel,
  suggestedMembers,
  subscriptionCost,
  planPeriod,
  isRestrictedFolder,
  totalNewUsers,
}: MemberAccessFormProps) => {
  // 300ms is the length of the AccordionDialog collapse/expansion transition
  const deferredRenderIsReady = useTimeDeferredMount(310);
  const uid = useUID();
  const listFormId = `invite-suggested-users-list-${uid}`;
  const inputRef = useRef<HTMLInputElement>(undefined);
  const [inputHasFocus, setInputHasFocus] = useState(false);
  const listTitle = searchInputTitle ?? TEXT.search.title;
  const isFolderAsset = resourceType === 'FolderAsset';
  const isUnrestrictedFolder = isFolderAsset && !isRestrictedFolder;
  const isFolderWithNoSuggestedMembers =
    isFolderAsset && inputTokens.length === 0 && suggestedMembers?.length === 0;
  const shouldShowMemberSeatRate = !useIsEnterpriseAccount();

  return (
    <>
      <StyledDiv $hasSuggestedMembers={Boolean(suggestedMembers)}>
        <Box display="flex" flexDirection="column" height="100%">
          {isUnrestrictedFolder && (
            <>
              <DisabledScrim />
              <Box paddingBottom={16} paddingX={16} style={{ zIndex: 1 }}>
                <AlertInline>{TEXT.restrictedFolderAlertText}</AlertInline>
              </Box>
            </>
          )}
          {!isUnrestrictedFolder && isFolderWithNoSuggestedMembers && (
            <>
              <DisabledScrim />
              <Box paddingBottom={16} paddingX={16} style={{ zIndex: 1 }}>
                <AlertInline>{TEXT.noSuggestedMembersForFolder}</AlertInline>
              </Box>
            </>
          )}
          <Box
            display="flex"
            flexDirection="column"
            flexShrink="0"
            paddingX={16}
            style={{ gap: theme.spaces[8] }}
          >
            <UserAccessSearchInput
              ref={mergeRefs([customInputRef, inputRef])}
              adornmentAfter={
                !canInviteGuests && availableRoles ? (
                  <Box alignItems="center" display="flex" height={32}>
                    <MemberAccessRoleSelect
                      availableRoles={availableRoles}
                      onChange={onChangeRole}
                      roleId={currentRoleId}
                    />
                  </Box>
                ) : undefined
              }
              adornmentUnder={
                canInviteGuests ? (
                  <LicenseAndRoleSelect
                    availableRoles={availableRoles}
                    licenseType={licenseType}
                    onLicenseChange={onChangeLicenseType}
                    onRoleChange={onChangeRole}
                    roleId={currentRoleId}
                  />
                ) : undefined
              }
              isDisabled={isUnrestrictedFolder}
              onChange={onSearchQueryChange}
              onEnterKey={hasNoClientSideMatches ? noop : onEnterKey}
              onInputBlur={(event: React.FocusEvent<HTMLInputElement>) => {
                setInputHasFocus(false);
                onInputBlur?.(event);
              }}
              onInputFocus={() => setInputHasFocus(true)}
              onPaste={onSearchQueryPaste}
              onRemoveToken={onRemoveMember}
              placeholder={searchInputPlaceholder ?? TEXT.search.placeholder}
              textQuery={currentInputQuery}
              tokens={inputTokens}
            />

            {licenseType ? (
              <MemberAccessInputDetails
                guestSeatLimit={isGuestOverLimit ? guestSeatLimit : undefined}
                hasNewMembers={hasNewMembers}
                invalidEmail={hasInvalidEmails}
                isOnTrial={isOnTrial}
                licenseType={licenseType}
                memberSeatLimit={
                  isMemberOverLimit ? memberSeatLimit : undefined
                }
                memberSeatRate={getMemberSeatRateLabel({
                  canShowRate: shouldShowMemberSeatRate,
                  planPeriod,
                  subscriptionCost,
                })}
                resourceType={resourceType}
                totalNewUsers={totalNewUsers}
              />
            ) : null}
          </Box>

          {suggestedMembers && (
            <>
              <Box paddingBottom={8} paddingTop={8} paddingX={16}>
                <Text color="tertiary" id={listFormId} variant="body">
                  {hasNoClientSideMatches
                    ? TEXT.search.noMatchesTitle
                    : listTitle}
                </Text>
              </Box>
              <ScrollContainer
                enableTopShadow={true}
                flexGrow="1"
                flexShrink="1"
                height={scrollContainerHeight}
                shadowSize="small"
              >
                {hasNoClientSideMatches ? (
                  <>
                    <Box
                      alignItems="center"
                      display="inline-flex"
                      padding={8}
                      width="100%"
                    >
                      <Box display="inline-block">
                        <IconMagnifyingglass
                          color={theme.colors.icon.interactive.off}
                          style={{ marginRight: theme.spaces[16] }}
                        />
                      </Box>

                      <Text
                        color="tertiary"
                        truncate={true}
                        variant="secondary"
                      >
                        {TEXT.search.noMatches(currentInputQuery)}
                      </Text>
                    </Box>
                    <Box paddingLeft={48} paddingRight={4}>
                      <Separator color="tertiary" gap={8} />
                    </Box>
                  </>
                ) : (
                  <AnimatePresence
                    key="suggested-members-list"
                    initial={false}
                    mode="wait"
                  >
                    {(!deferRender || deferredRenderIsReady) && (
                      <motion.div
                        animate={{
                          opacity: isLoading ? 0 : 1,
                        }}
                        initial={{ opacity: 0 }}
                        transition={{
                          delay: isLoading ? 0.6 : 0,
                          duration: 0.2,
                        }}
                      >
                        <MemberAccessRowList
                          controlledByElement={inputRef.current}
                          highlightFirstRow={inputHasFocus}
                          label={listTitle}
                          matches={suggestedMembers}
                          onMemberClick={onMemberClick}
                          onPendingMemberClick={onPendingMemberClick}
                          textInput={currentInputQuery}
                        />
                      </motion.div>
                    )}
                  </AnimatePresence>
                )}
                <Spinner
                  aria-label={TEXT.labelSpinner}
                  centered={true}
                  isLoading={isLoading}
                />
              </ScrollContainer>
            </>
          )}

          <Separator color="secondary" />
          {shouldManageEmailMessage && (
            <>
              <EmailMessageTextarea
                autosize={true}
                data-testid="FormDialog-message"
                disabled={isUnrestrictedFolder}
                onChange={onEmailMessageChange}
                placeholder={TEXT.message.placeholder}
                rowsMax={6.5}
                rowsMin={2.5}
                value={message}
              />
              <Box position="relative" right="18px" top="-42px">
                <GraphemeLimit
                  graphemeCount={message.length}
                  graphemeMaximumCount={INVITE_MESSAGE_MAX_LENGTH}
                />
              </Box>
            </>
          )}

          <DialogFooter $showTopBorder={false}>
            <DialogActions>
              <Button onClick={onCancel}>{TEXT.labelCancel}</Button>
              <Button
                color="primary"
                disabled={isUnrestrictedFolder || isSubmitDisabled}
                onClick={() => onSubmit(licenseType)}
              >
                {submitLabel ?? TEXT.labelSubmit}
              </Button>
            </DialogActions>
          </DialogFooter>
        </Box>
      </StyledDiv>
    </>
  );
};
const StyledDiv = styled.div<{
  $hasSuggestedMembers;
}>`
  height: ${(p) => (p.$hasSuggestedMembers ? '447px' : 'auto')};
  position: relative;
`;

const LicenseRoleContainer = styled(Box)`
  border-top: 1px solid ${theme.colors.border.primary};
  height: 40px;
`;

const UpgradeButton = styled(Button)`
  ${textStyles.caption};
  color: ${theme.colors.text.link};
  cursor: pointer;
`;

const DisabledScrim = styled.div`
  background-color: ${theme.colors.components.modal.background};
  bottom: 0;
  left: 0;
  opacity: 50%;
  pointer-events: none;
  position: absolute;
  right: 0;
  top: 0;
`;
